import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import AnimatedCounter from "../components/admin/AnimatedCounter";
import GuestsTab from "../components/admin/GuestsTab";
import BadgesAdminTab from "../components/admin/BadgesAdminTab";
import AnnouncementsTab from "../components/admin/AnnouncementsTab";
import ScheduleTab from "../components/admin/ScheduleTab";
import VolunteerScheduleTab from "../components/admin/VolunteerScheduleTab";
import SettingsTab from "../components/admin/SettingsTab";
import RegistryTab from "../components/admin/RegistryTab";
import AdminProfileDialog from "../components/admin/AdminProfileDialog";
import { DailyDigestDialog } from "../components/admin/DailyDigestDialog";
import AdminPushButton from "../components/admin/AdminPushButton";
import {
  ArrowLeft,
  Users,
  Calendar,
  Bell,
  Check,
  Loader2,
  RefreshCw,
  Settings,
  CalendarDays,
  Award,
  Megaphone,
  Table,
  ClipboardList,
} from "lucide-react";

const AdminDashboard = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [guests, setGuests] = useState([]);
  const [stats, setStats] = useState({});
  const [announcements, setAnnouncements] = useState([]);
  const [weeklySchedule, setWeeklySchedule] = useState({ templates: [], calendar: [] });
  const [espressoTalks, setEspressoTalks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "overview");
  const [pendingBadgeRequestsCount, setPendingBadgeRequestsCount] = useState(0);
  const [pendingEventsCount, setPendingEventsCount] = useState(0);
  const [usersPoints, setUsersPoints] = useState([]);
  const [loadingPoints, setLoadingPoints] = useState(false);
  const [showProfileSettings, setShowProfileSettings] = useState(false);
  const [showDailyDigest, setShowDailyDigest] = useState(false);
  const [adminAvatar, setAdminAvatar] = useState(user?.avatar || "");

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchData();
    fetchAdminProfile();
    fetchPendingBadgeRequestsCount();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [guestsRes, statsRes, announcementsRes, scheduleRes, talksRes, pendingRes] = await Promise.all([
        axios.get(`${API}/admin/guests`, { headers }),
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/announcements`, { headers }),
        axios.get(`${API}/community/weekly-schedule`, { headers }).catch(() => ({ data: { templates: [], calendar: [] } })),
        axios.get(`${API}/community/espresso-talks`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/events/pending`, { headers }).catch(() => ({ data: [] }))
      ]);

      setGuests(guestsRes.data);
      setStats(statsRes.data);
      setAnnouncements(announcementsRes.data);
      setWeeklySchedule(scheduleRes.data || { templates: [], calendar: [] });
      setEspressoTalks(talksRes.data || []);
      setPendingEventsCount((pendingRes.data || []).length);
    } catch (error) {
      console.error("Error:", error);
      if (error.response?.status === 403) {
        toast.error("Admin access required");
        navigate("/dashboard");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchAdminProfile = async () => {
    try {
      const res = await axios.get(`${API}/admin/profile`, { headers });
      if (res.data.avatar) {
        setAdminAvatar(res.data.avatar);
      }
    } catch (error) {
      console.error("Error fetching admin profile:", error);
    }
  };

  const fetchPendingBadgeRequestsCount = async () => {
    try {
      const res = await axios.get(`${API}/passport/requests`, { headers });
      setPendingBadgeRequestsCount((res.data.requests || []).length);
    } catch (error) {
      console.error("Error fetching badge requests count:", error);
    }
  };

  const fetchUsersPoints = async () => {
    setLoadingPoints(true);
    try {
      const res = await axios.get(`${API}/admin/passport/all-points`, { headers });
      setUsersPoints(res.data.users || []);
    } catch (error) {
      console.error("Error fetching users points:", error);
    } finally {
      setLoadingPoints(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#E07A5F]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFCF8] savi-bg-pattern pb-20">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-stone-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-4">
            <Link to="/dashboard" className="text-[#3D4035]/60 hover:text-[#3D4035]">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-2 sm:gap-3">
              <img
                src="https://customer-assets.emergentagent.com/job_184a0a73-7ed5-43f8-9a23-0cd69b83d3bb/artifacts/rcargp5m_no%20bkg.png"
                alt="Savi"
                className="h-6 sm:h-8 w-auto"
              />
              <div>
                <h1 className="font-serif text-base sm:text-lg text-[#3D4035]">Admin</h1>
                <p className="text-xs text-[#3D4035]/60 hidden sm:block">Welcome, {user?.name}</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1 sm:gap-2">
            <AdminPushButton />
            <Button
              size="sm"
              onClick={() => setShowDailyDigest(true)}
              className="bg-[#E07A5F] hover:bg-[#D06A4F] text-white rounded-full text-xs sm:text-sm"
              data-testid="daily-digest-btn"
            >
              <Megaphone className="w-4 h-4 sm:mr-1.5" />
              <span className="hidden sm:inline">Today at Savi</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchData}
              className="text-[#3D4035]/60 p-2"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
            <button
              onClick={() => setShowProfileSettings(true)}
              className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-[#F4F1DE] flex items-center justify-center overflow-hidden hover:ring-2 hover:ring-[#E07A5F] transition-all"
              data-testid="admin-profile-btn"
            >
              {adminAvatar ? (
                <img src={adminAvatar} alt="Admin" className="w-full h-full object-cover" />
              ) : (
                <span className="text-sm sm:text-lg font-serif text-[#E07A5F]">
                  {user?.name?.charAt(0) || "A"}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Stats */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 mb-4 sm:mb-8">
          <div className="bg-white rounded-xl sm:rounded-2xl p-3 sm:p-5 border border-stone-100">
            <Users className="w-5 h-5 sm:w-6 sm:h-6 text-[#E07A5F] mb-1 sm:mb-2" />
            <p className="text-2xl sm:text-3xl font-serif text-[#3D4035]">
              <AnimatedCounter value={stats.total_guests || 0} />
            </p>
            <p className="text-xs sm:text-sm text-[#3D4035]/60">Guests</p>
          </div>
          <div className="bg-white rounded-xl sm:rounded-2xl p-3 sm:p-5 border border-stone-100">
            <Check className="w-5 h-5 sm:w-6 sm:h-6 text-[#81B29A] mb-1 sm:mb-2" />
            <p className="text-2xl sm:text-3xl font-serif text-[#3D4035]">
              <AnimatedCounter value={stats.completed_onboarding || 0} />
            </p>
            <p className="text-xs sm:text-sm text-[#3D4035]/60">Completed</p>
          </div>
          <div className="bg-white rounded-xl sm:rounded-2xl p-3 sm:p-5 border border-stone-100">
            <CalendarDays className="w-5 h-5 sm:w-6 sm:h-6 text-[#3D4035] mb-1 sm:mb-2" />
            <p className="text-2xl sm:text-3xl font-serif text-[#3D4035]">
              <AnimatedCounter value={stats.upcoming_events || 0} />
            </p>
            <p className="text-xs sm:text-sm text-[#3D4035]/60">Events</p>
          </div>
          <div className="bg-white rounded-xl sm:rounded-2xl p-3 sm:p-5 border border-stone-100">
            <Calendar className="w-5 h-5 sm:w-6 sm:h-6 text-[#E07A5F] mb-1 sm:mb-2" />
            <p className="text-2xl sm:text-3xl font-serif text-[#3D4035]">
              <AnimatedCounter value={stats.pending_arrivals || 0} />
            </p>
            <p className="text-xs sm:text-sm text-[#3D4035]/60">Pending</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 sm:gap-2 mb-4 sm:mb-6 overflow-x-auto no-scrollbar pb-1">
          {[
            { id: "overview", label: "Guests", icon: Users },
            { id: "registry", label: "Registry", icon: Table },
            { id: "badges", label: "Badges", icon: Award, badge: pendingBadgeRequestsCount },
            { id: "schedule", label: "Schedule", icon: CalendarDays, badge: pendingEventsCount },
            { id: "volunteers", label: "Team", icon: ClipboardList },
            { id: "announcements", label: "News", icon: Bell },
            { id: "settings", label: "Settings", icon: Settings }
          ].map(({ id, label, icon: Icon, badge: badgeCount }) => (
            <Button
              key={id}
              variant={activeTab === id ? "default" : "ghost"}
              onClick={() => {
                setActiveTab(id);
                if (id === 'badges') {
                  fetchPendingBadgeRequestsCount();
                  fetchUsersPoints();
                }
              }}
              size="sm"
              className={`rounded-full whitespace-nowrap text-xs sm:text-sm ${activeTab === id ? "bg-[#3D4035] text-white" : "text-[#3D4035]/60"}`}
              data-testid={`tab-${id}`}
            >
              <div className="relative">
                <Icon className="w-3 h-3 sm:w-4 sm:h-4" />
                {badgeCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center">
                    {badgeCount > 9 ? '9+' : badgeCount}
                  </span>
                )}
              </div>
              <span className="ml-1 sm:ml-2">{label}</span>
            </Button>
          ))}
        </div>

        {activeTab === "overview" && (
          <GuestsTab guests={guests} headers={headers} onRefresh={fetchData} />
        )}

        {activeTab === "registry" && (
          <RegistryTab guests={guests} headers={headers} onRefresh={fetchData} />
        )}

        {activeTab === "badges" && (
          <BadgesAdminTab token={token} guests={guests} onNotificationChange={fetchPendingBadgeRequestsCount} usersPoints={usersPoints} loadingPoints={loadingPoints} fetchUsersPoints={fetchUsersPoints} />
        )}

        {activeTab === "announcements" && (
          <AnnouncementsTab announcements={announcements} headers={headers} onRefresh={fetchData} />
        )}

        {activeTab === "schedule" && (
          <ScheduleTab weeklySchedule={weeklySchedule} espressoTalks={espressoTalks} guests={guests} headers={headers} onRefresh={fetchData} />
        )}

        {activeTab === "volunteers" && (
          <VolunteerScheduleTab headers={headers} />
        )}

        {activeTab === "settings" && (
          <SettingsTab headers={headers} />
        )}
      </div>

      <DailyDigestDialog
        open={showDailyDigest}
        onOpenChange={setShowDailyDigest}
        headers={headers}
        onPosted={fetchData}
      />

      <AdminProfileDialog
        open={showProfileSettings}
        onOpenChange={setShowProfileSettings}
        user={user}
        headers={headers}
        logout={logout}
        adminAvatar={adminAvatar}
        setAdminAvatar={setAdminAvatar}
      />
    </div>
  );
};

export default AdminDashboard;
